import { useMemo, useCallback } from 'react';
import { useTranslate } from '@tolgee/react';
import { gt } from 'lodash-es';
import dayjs from 'dayjs';
import { message } from 'antd';
import { getCurrencySymbol } from 'utils/getCurrencySymbol';
import copyText from 'utils/copyText';
import { getTodoStillTime } from 'utils/renderStill';

/** 待办详情「额外字段」业务数据与行为，与 Formik 解耦，便于在不同 UI 中复用 */
export interface ExtraFieldsBag {
  /** 货币符号 */
  unit: string;
  /** 是否有任意一块内容可展示（用于外层 empty:hidden 等） */
  hasContent: boolean;
  /** 赔偿金额：展示条件为有值且 > 0 */
  compensation: {
    visible: boolean;
    amount: number;
  };
  /** type === 8：订单编号、物流单号、卖家支付运费 */
  type8: {
    visible: boolean;
    relatedTradeId: string;
    relatedTraceNum: string;
    freightAmount: number;
  };
  /** type === 9：订单编号、下单时间、等待发货时长 */
  type9: {
    visible: boolean;
    relatedTradeId: string;
    orderTime: string;
    orderTimeFormatted: string;
    stillTime: string;
  };
  /** type === 10：订单编号、物流单号、下单时间、等待揽收时长 */
  type10: {
    visible: boolean;
    relatedTradeId: string;
    relatedTraceNum: string;
    orderTime: string;
    orderTimeFormatted: string;
    stillTime: string;
  };
  /** type === 11：订单编号、物流单号、发货时间、运输时长 */
  type11: {
    visible: boolean;
    relatedTradeId: string;
    relatedTraceNum: string;
    deliveryTime: string;
    deliveryTimeFormatted: string;
    stillTime: string;
  };
  /** 复制文案并提示成功，UI 可绑定到任意可复制字段 */
  onCopy: (text: string) => Promise<void>;
}

/**
 * 待办详情「额外字段」业务逻辑 hook。
 * 不依赖 Formik，仅根据 todo 实例计算展示块与派生数据，便于在其他模块用不同 UI 渲染。
 *
 * @param value 待办实例（或满足字段的子集）
 * @returns ExtraFieldsBag 供任意 UI 消费
 */
export function useExtraFields(value: Todo.TodoInstance | null | undefined): ExtraFieldsBag {
  const { t } = useTranslate();

  const unit = useMemo(() => getCurrencySymbol(value?.currency ?? ''), [value?.currency]);

  const compensation = useMemo(() => {
    const amount = value?.compensationAmount ?? 0;
    return {
      visible: Boolean(amount && gt(amount, 0)),
      amount,
    };
  }, [value?.compensationAmount]);

  const type8 = useMemo(() => {
    const type = value?.type;
    const visible = type === 8;
    return {
      visible,
      relatedTradeId: value?.relatedTradeId ?? '',
      relatedTraceNum: value?.relatedTraceNum ?? '',
      freightAmount: value?.freightAmount ?? 0,
    };
  }, [value?.type, value?.relatedTradeId, value?.relatedTraceNum, value?.freightAmount]);

  const type9 = useMemo(() => {
    const type = value?.type;
    const visible = type === 9;
    const orderTime = value?.orderTime ?? '';
    const stillTime = value ? getTodoStillTime(t, value) : '';
    return {
      visible,
      relatedTradeId: value?.relatedTradeId ?? '',
      orderTime,
      orderTimeFormatted: orderTime ? dayjs(Number(orderTime)).format('YYYY-MM-DD HH:mm') : '',
      stillTime,
    };
  }, [t, value]);

  const type10 = useMemo(() => {
    const type = value?.type;
    const visible = type === 10;
    const orderTime = value?.orderTime ?? '';
    const stillTime = value ? getTodoStillTime(t, value) : '';
    return {
      visible,
      relatedTradeId: value?.relatedTradeId ?? '',
      relatedTraceNum: value?.relatedTraceNum ?? '',
      orderTime,
      orderTimeFormatted: orderTime ? dayjs(Number(orderTime)).format('YYYY-MM-DD HH:mm') : '',
      stillTime,
    };
  }, [t, value]);

  const type11 = useMemo(() => {
    const type = value?.type;
    const visible = type === 11;
    const deliveryTime = value?.deliveryTime ?? '';
    const stillTime = value ? getTodoStillTime(t, value) : '';
    return {
      visible,
      relatedTradeId: value?.relatedTradeId ?? '',
      relatedTraceNum: value?.relatedTraceNum ?? '',
      deliveryTime,
      deliveryTimeFormatted: deliveryTime
        ? dayjs(Number(deliveryTime)).format('YYYY-MM-DD HH:mm')
        : '',
      stillTime,
    };
  }, [t, value]);

  const hasContent = useMemo(
    () =>
      compensation.visible || type8.visible || type9.visible || type10.visible || type11.visible,
    [compensation.visible, type8.visible, type9.visible, type10.visible, type11.visible],
  );

  const onCopy = useCallback(
    (text: string) => {
      return copyText(text).then(() => message.success(t('common.feedback.copySuccess')));
    },
    [t],
  );

  return {
    unit,
    hasContent,
    compensation,
    type8,
    type9,
    type10,
    type11,
    onCopy,
  };
}
